import { hasKey, t } from '@app/i18n';
import type { Causa, Diagnostic } from './causa';

/**
 * A frase por eixo de uma decisão (T17) ou simulação (T18), composta da ESTRUTURA do
 * diagnóstico no idioma ativo — marco 4 da qa-i18n (09/09).
 *
 * # Por que não o `detail`
 *
 * O `diagnostico.detail` nasce no Rego (`infra-aig/opa/authz.rego › _detalhe_do_eixo` e
 * `_detalhe_da_delegacao`), em pt-BR, e o middleware copia. É texto do servidor, da mesma
 * classe dos rótulos de T20 e da linha A2A de T10: com o console em `en`, o mrolim leu
 * «eixo pessoa: nenhuma política com condição de claim casa com quem pede · …». O Rego não
 * muda neste lote (o `detail` continua no log); o que muda é quem frasea.
 *
 * # O mapa é o do Rego, e é total
 *
 * O Rego só escreve `detail` quando ao menos um eixo barrou — quatro ramos por alvo, um por
 * combinação `workload × pessoa`, `null` quando os dois passam. As três frases (por tool e
 * por delegação) estão aqui como chaves, **pt-BR byte a byte** ao que o Rego emite hoje; o
 * `en` é novo. A escolha do ramo vem dos flags `satisfied` dos dois eixos, não do texto.
 *
 * # Causa que não é de eixo
 *
 * Barreira (`identity_not_attested`, `token_*`), protocolo (`rpc_method_not_allowed`),
 * módulo manual e `unknown` não têm frase por eixo: a explicação é a da própria causa
 * (`decisions.cause.<causa>.tip`), que o catálogo já tinha. `allowed` não explica nada —
 * como no Rego — e devolve `null`.
 *
 * O `detail` cru NÃO entra aqui: quem quiser mostrá-lo o faz como dado do executor, em
 * linha secundária, nunca como a frase principal.
 */

/** O alvo da decisão: uma tool (`tools/call`) ou o agente de uma delegação (DEF-29). */
export interface DiagnosticTarget {
  kind: 'tool' | 'agent';
  name: string | null | undefined;
}

/** Causas que o Rego decide nos EIXOS — as únicas que ganham a frase composta. */
const AXIS_CAUSES: ReadonlySet<string> = new Set([
  'workload_axis',
  'person_axis',
  'a2a_not_granted',
]);

function axisPhrase(
  diagnostico: Diagnostic | null | undefined,
  alvo: DiagnosticTarget,
): string | null {
  const workload = diagnostico?.workload_axis?.satisfied;
  const person = diagnostico?.person_axis?.satisfied;
  if (typeof workload !== 'boolean' || typeof person !== 'boolean') return null;
  if (workload && person) return null;
  const kind = alvo.kind === 'agent' ? 'delegation' : 'tool';
  const target =
    alvo.name ??
    diagnostico?.agent ??
    t(
      alvo.kind === 'agent'
        ? 'decisions.context.identityFallback'
        : 'decisions.context.toolFallback',
    );
  const rule = diagnostico?.person_axis?.rule ?? t('decisions.context.ruleFallback');
  const branch = !workload
    ? person
      ? 'workloadDenied.personSatisfied'
      : 'workloadDenied.personDenied'
    : 'personDenied.workloadSatisfied';
  return t(`decisions.diagnostic.${kind}.${branch}`, { target, rule });
}

/**
 * A frase principal do «Por que negou», ou `null` quando não há o que explicar.
 *
 * Ordem: causa de eixo → frase composta pelos flags; causa de barreira/protocolo/manual/
 * `unknown` → a explicação da causa; sem causa (o `simulate` do mock ainda não a publica)
 * → composta pelos flags, se os eixos foram avaliados.
 */
export function diagnosticText(
  causa: Causa | null | undefined,
  diagnostico: Diagnostic | null | undefined,
  alvo: DiagnosticTarget,
): string | null {
  if (causa === 'allowed') return null;
  // `unknown` é «o PDP não carimbou causa», não uma causa: com eixos (janela do rollout,
  // Rego novo ainda não subiu em todo lugar) a frase nasce deles; sem eixos não há frase —
  // o Alert «Sem diagnóstico» do WhyDenied já diz isso, e o tip de `unknown` fica no
  // tooltip do badge. Devolver o tip aqui punha a mesma frase duas vezes na tela.
  const semCausa = !causa || causa === 'unknown';
  if (!semCausa && !AXIS_CAUSES.has(causa)) {
    const key = `decisions.cause.${causa}.tip`;
    return hasKey(key) ? t(key) : null;
  }
  const composed = axisPhrase(diagnostico, alvo);
  if (composed) return composed;
  if (!semCausa) {
    const key = `decisions.cause.${causa}.tip`;
    return hasKey(key) ? t(key) : null;
  }
  return null;
}
